import {
  documentViewerKind,
  type PdfHighlight,
  type XlsxRowHighlight,
} from "@volca/ui/components/ui/document-viewer";
import { DocumentOriginal } from "@/profitability/documents/document-original";
import { RefusedWorkbook } from "@/profitability/documents/refused-workbook";
import { SourcePreview } from "./source-preview";
import { citationPlace, documentCitation } from "./sourced";
import type { FilePart, RunCitation } from "./types";

/**
 * One file a figure came from, drawn by the viewer for its kind and opened
 * at the part being looked at (`selected`: its cell outlined), with every
 * other row of the figure in that file highlighted at once: a spreadsheet's
 * rows tinted, a PDF's boxed, and a row shown but not counted dashed. Text,
 * or a CSV too large to draw, shows its rows as read at the selected one.
 */
export const SourceFile = ({
  businessId,
  file,
  parts,
  selected,
}: {
  businessId: string;
  /** The file: a part of it, or the column's file opened without a row. */
  file: RunCitation;
  /** The figure's parts in this file. */
  parts: FilePart[];
  /** The part to open at; null opens the file at its top. */
  selected: RunCitation | null;
}) => {
  const kind = documentViewerKind({
    contentType: file.content_type,
    fileName: file.file_name,
  });
  const target = selected && selected.row !== null ? selected : null;
  const citation = target ? documentCitation(target, kind) : null;
  const place = target ? citationPlace(target) : null;

  const rows =
    kind === "xlsx" || kind === "csv"
      ? parts.flatMap((part): XlsxRowHighlight[] => {
          const at = documentCitation(part, kind);
          return at.row
            ? [
                {
                  sheet: at.sheet,
                  row: at.row,
                  tone: part.used ? "info" : "muted",
                },
              ]
            : [];
        })
      : undefined;
  // The selected part's box first: the page scrolls to it, and only it is named.
  const ordered = [
    ...parts.filter((part) => part === selected),
    ...parts.filter((part) => part !== selected),
  ];
  const highlights =
    kind === "pdf" && parts.length
      ? ordered.flatMap((part): PdfHighlight[] => {
          const at = documentCitation(part, kind);
          return at.page && at.box
            ? [
                {
                  page: at.page,
                  box: at.box,
                  tone: part.used ? "info" : "muted",
                  label:
                    part === selected
                      ? (citationPlace(part, false) ?? undefined)
                      : undefined,
                },
              ]
            : [];
        })
      : undefined;

  if (!kind)
    return (
      <SourcePreview
        // A citation into another table opens on that table.
        key={`${file.document_id}:${target?.sheet}`}
        businessId={businessId}
        documentId={file.document_id}
        citation={target ? documentCitation(target, null) : {}}
      />
    );
  return (
    <DocumentOriginal
      businessId={businessId}
      document={{ id: file.document_id, file_name: file.file_name }}
      kind={kind}
      citation={citation}
      highlightLabel={place ?? undefined}
      highlights={highlights}
      rows={rows}
      className="min-h-0 flex-1"
      fallback={(reason) => (
        <RefusedWorkbook reason={reason}>
          <SourcePreview
            businessId={businessId}
            documentId={file.document_id}
            citation={target ? documentCitation(target, null) : {}}
          />
        </RefusedWorkbook>
      )}
    />
  );
};
